import React, { useCallback, useMemo, useReducer } from 'react';

export type VideoPlayerContextValues = {
    parentalLockIsVisible: boolean;
    disableUi: boolean;
    displayEpisodePicker: boolean;
    endScreenVisible: boolean;
    autoPlayWasBlocked: boolean;
    videoSqueeze: boolean;
};

const defaultValues = {
    disableUi: false,
    parentalLockIsVisible: false,
    displayEpisodePicker: false,
    endScreenVisible: false,
    autoPlayWasBlocked: false,
    videoSqueeze: false,
};

type SetContextValue = <K extends keyof VideoPlayerContextValues>(
    key: K,
    value: VideoPlayerContextValues[K]
) => void;

export const VideoPlayerContext = React.createContext<
    VideoPlayerContextValues & {
        setContext: SetContextValue;
    }
>({
    ...{
        ...defaultValues,
        ...{
            setContext: () => {
                /**/
            },
        },
    },
});

const getMemoizedState = <K extends keyof VideoPlayerContextValues>(
    state: VideoPlayerContextValues,
    key: K,
    value: VideoPlayerContextValues[K]
): VideoPlayerContextValues => {
    if (state[key] === value) {
        return state;
    }

    return {
        ...state,
        [key]: value,
    };
};

const VideoPlayerContextReducer = <K extends keyof VideoPlayerContextValues>(
    state: VideoPlayerContextValues,
    action: { type: K; payload: VideoPlayerContextValues[K] }
): VideoPlayerContextValues => {
    switch (action.type) {
        case 'disableUi':
            return getMemoizedState(state, 'disableUi', action.payload || state.parentalLockIsVisible);
        default:
            return getMemoizedState(state, action.type, action.payload);
    }
};

const VideoPlayerContextProvider: React.FC<unknown> = ({ children }): React.ReactElement => {
    const [state, dispatch] = useReducer(VideoPlayerContextReducer, defaultValues);

    const setContext = useCallback(
        (key, value) => {
            dispatch({ type: key, payload: value });
        },
        [dispatch]
    );

    const value = useMemo(
        () => ({
            ...{
                ...state,
                ...{
                    setContext,
                },
            },
        }),
        [state, setContext]
    );

    return <VideoPlayerContext.Provider value={value}>{children}</VideoPlayerContext.Provider>;
};

export default VideoPlayerContextProvider;
